<script setup>
import { ref, computed, watch } from 'vue';
import wootConstants from 'dashboard/constants/globals';
import ClassificationBadge from './ClassificationBadge.vue';

const props = defineProps({
  initialState: {
    type: Object,
    default: () => ({}),
  },
  customClassifications: {
    type: Array,
    default: () => [],
  },
  maxContentHeight: {
    type: String,
    default: '400px',
  },
});

const emit = defineEmits(['filter']);

const showMenu = ref({
  sort: false,
});

const activeOption = ref({
  sort: wootConstants.SORT_LABELS.NEWEST,
});

const filter = ref({});

const classificationOptions = [
  {
    name: wootConstants.INTERPRETATION_TYPES.POSITIVE,
    key: wootConstants.INTERPRETATION_TYPES.POSITIVE,
    selected: false,
    type: wootConstants.FILTER_TYPE.CLASSIFICATION,
  },
  {
    name: wootConstants.INTERPRETATION_TYPES.NEGATIVE,
    key: wootConstants.INTERPRETATION_TYPES.NEGATIVE,
    selected: false,
    type: wootConstants.FILTER_TYPE.CLASSIFICATION,
  },
  {
    name: wootConstants.INTERPRETATION_TYPES.NEUTRAL,
    key: wootConstants.INTERPRETATION_TYPES.NEUTRAL,
    selected: false,
    type: wootConstants.FILTER_TYPE.CLASSIFICATION,
  },
  {
    name: wootConstants.INTERPRETATION_TYPES.IRONY,
    key: wootConstants.INTERPRETATION_TYPES.IRONY,
    selected: false,
    type: wootConstants.FILTER_TYPE.CLASSIFICATION,
  },
  {
    name: wootConstants.INTERPRETATION_TYPES.SPAM,
    key: wootConstants.INTERPRETATION_TYPES.SPAM,
    selected: false,
    type: wootConstants.FILTER_TYPE.CLASSIFICATION,
  },
  {
    name: wootConstants.INTERPRETATION_TYPES.UNKNOWN,
    key: wootConstants.INTERPRETATION_TYPES.UNKNOWN,
    selected: false,
    type: wootConstants.FILTER_TYPE.CLASSIFICATION,
  },
];

const sortOptions = [
  {
    name: wootConstants.SORT_LABELS.NEWEST,
    key: wootConstants.INBOX_SORT_BY.NEWEST,
    type: wootConstants.INBOX_FILTER_TYPE.SORT_ORDER,
  },
  {
    name: wootConstants.SORT_LABELS.OLDEST,
    key: wootConstants.INBOX_SORT_BY.OLDEST,
    type: wootConstants.INBOX_FILTER_TYPE.SORT_ORDER,
  },
];

const getCustomClassifications = computed(() => {
  const formattedClassifications = [];
  props.customClassifications.forEach(classification => {
    formattedClassifications.push({
      name: classification,
      key: classification,
      selected: false,
      type: 'customClassifications',
    });
  });
  return formattedClassifications.sort((a, b) => a.name.localeCompare(b.name));
});

const addOrRemoveFromStack = (stack, value, prop) => {
  if (!stack[prop]) {
    stack[prop] = [];
  }
  if (stack[prop].find(e => e.key === value.key)) {
    stack[prop] = stack[prop].filter(item => item.key !== value.key);
  } else {
    stack[prop].push(value);
  }
};

const isChecked = (value, prop) => {
  return filter.value[prop]?.find(item => item.key === value.key);
};

const onFilterChange = (value, prop) => {
  if (['classification', 'customClassifications'].includes(prop)) {
    addOrRemoveFromStack(filter.value, value, prop);
  } else {
    filter.value[prop] = value;
  }
  emit('filter', filter.value);
};

const onMenuOpen = menu => {
  showMenu.value[menu] = !showMenu.value[menu];
};

const onMenuOptionClick = (option, menu) => {
  activeOption.value[menu] = option.name;
  showMenu.value[menu] = false;
  onFilterChange(option, menu);
};

watch(
  () => props.initialState,
  newState => {
    filter.value = newState;
    if (!filter.value.sort) {
      filter.value.sort = sortOptions[0];
    }
    activeOption.value = {
      sort: filter.value.sort.name,
    };
  },
  { immediate: true }
);
</script>

<template>
  <div
    class="flex flex-col bg-white dark:bg-slate-800 w-full sm:w-[420px] md:w-[480px] border shadow-lg border-slate-200 dark:border-slate-700/50 rounded-xl overflow-hidden"
  >
    <!-- Sort Section -->
    <div
      class="flex flex-col gap-2 p-4 border-b border-slate-100 dark:border-slate-700/50"
    >
      <label
        class="text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wide"
      >
        {{ $t('COMMENTS.FILTER.SORT_BY') }}
      </label>
      <div class="relative">
        <button
          type="button"
          class="w-full h-10 flex items-center justify-between px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent"
          @click="onMenuOpen('sort')"
        >
          <span>{{ activeOption.sort }}</span>
          <fluent-icon
            icon="chevron-down"
            size="16"
            class="text-slate-500 dark:text-slate-400 transition-transform"
            :class="{ 'rotate-180': showMenu.sort }"
          />
        </button>
        <div
          v-if="showMenu.sort"
          class="absolute top-full mt-1 w-full bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden"
        >
          <button
            v-for="option in sortOptions"
            :key="option.key"
            type="button"
            class="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
            :class="{
              'bg-woot-50 dark:bg-woot-900/20 text-woot-600 dark:text-woot-400':
                activeOption.sort === option.name,
              'text-slate-700 dark:text-slate-300':
                activeOption.sort !== option.name,
            }"
            @click.stop="onMenuOptionClick(option, 'sort')"
          >
            <span>{{ option.name }}</span>
            <fluent-icon
              v-if="activeOption.sort === option.name"
              icon="checkmark"
              size="16"
              class="text-woot-600 dark:text-woot-400"
            />
          </button>
        </div>
      </div>
    </div>

    <!-- Filters Content -->
    <div
      class="flex flex-col gap-5 p-4 overflow-y-auto"
      :style="{ maxHeight: maxContentHeight }"
    >
      <!-- Interpretation Filter -->
      <div class="flex flex-col gap-2">
        <h3
          class="text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wide"
        >
          {{ $t('COMMENTS.FILTER.INTERPRETATION') }}
        </h3>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <label
            v-for="option in classificationOptions"
            :key="option.key"
            class="flex items-center gap-2 cursor-pointer group"
          >
            <input
              :id="option.key"
              type="checkbox"
              :name="option.key"
              :checked="isChecked(option, option.type)"
              class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-woot-600 focus:ring-2 focus:ring-woot-500 focus:ring-offset-0 dark:bg-slate-900 dark:checked:bg-woot-600 dark:checked:border-woot-600"
              @change="onFilterChange(option, option.type)"
            />
            <ClassificationBadge :type="option.name" />
          </label>
        </div>
      </div>

      <!-- Custom Classifications Filter -->
      <div v-if="getCustomClassifications.length" class="flex flex-col gap-2">
        <h3
          class="text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wide"
        >
          {{ $t('COMMENTS.FILTER.CUSTOM_CLASSIFICATIONS') }}
        </h3>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <label
            v-for="option in getCustomClassifications"
            :key="option.key"
            class="flex items-center gap-2 cursor-pointer group"
          >
            <input
              :id="`custom-${option.key}`"
              type="checkbox"
              :name="option.key"
              :checked="isChecked(option, 'customClassifications')"
              class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-woot-600 focus:ring-2 focus:ring-woot-500 focus:ring-offset-0 dark:bg-slate-900 dark:checked:bg-woot-600 dark:checked:border-woot-600"
              @change="onFilterChange(option, 'customClassifications')"
            />
            <span
              class="text-xs font-medium text-slate-700 dark:text-slate-300 truncate group-hover:text-woot-600 dark:group-hover:text-woot-400 transition-colors"
            >
              {{ option.name }}
            </span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>
